import { useState } from 'react';

import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import { getWatersBySpecies } from '../../queries/rutilus/Species';
import { GetWatersBySpecies, GetWatersBySpeciesVariables } from '../../interfaces/graphql';
import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { useIsMobileScreenWidthOrThinner } from '../../helpers/hooks/useScreenWidth';
import { getImgProxyLoader } from '../../helpers/imageLoaders/imgProxy';
import { getSpeciesUrl } from '../../helpers/urls';
import { EventMykissCardClicked } from '../../interfaces/events';
import { IBoundingBox } from '../../interfaces/geo';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { FishCatchesIcon } from '../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../NextImage/NextImageWrapper';
import { Button } from '../Clickables/Buttons/Button';

interface IProps {
  boundingBox?: IBoundingBox;
  name: string;
  nrOfFishingSpots?: number;
  speciesId: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  className?: string;
  imageClassName?: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  useCustomLoader?: boolean;
}

export const TopSpeciesCard = ({
  boundingBox,
  speciesId,
  name,
  clickEventSource,
  nrOfFishingSpots,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  useCustomLoader = true,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const { currentPage } = useAnalytics();
  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth ?? 0, imageHeight, { quality });

  const handleSpeciesClick = () => {
    void trackEvent({
      name: 'mykiss_card_clicked',
      properties: {
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      },
    });
  };

  const handleTopWatersClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: clickEventSource,
        source_id: speciesId,
        position: clickEventPosition,
        page: currentPage,
        type: 'downloadModal',
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const { loading, data } = useRutilusQuery<GetWatersBySpecies, GetWatersBySpeciesVariables>(
    getWatersBySpecies,
    {
      variables: {
        boundingBox,
        speciesIDs: [speciesId],
      },
      skip: nrOfFishingSpots !== undefined || boundingBox === undefined,
    },
  );

  const hasFishingSpots: boolean =
    (nrOfFishingSpots !== undefined && nrOfFishingSpots > 0) ||
    (data?.entry.totalCount !== undefined && data.entry.totalCount > 0);

  return (
    <div
      className={twMerge(
        'border-b-twilight relative w-full border-b py-3 pr-2 md:py-2.5',
        className,
      )}
    >
      <div className="relative flex items-center gap-4">
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="w-32 h-12 md:h-11 md:w-28"
            imageClassName={twMerge('size-full object-contain', imageClassName)}
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={name}
            src={imageUrl}
            loader={useCustomLoader ? imgProxyLoader : undefined}
            fill
            sizes="(min-width: 990px) 128vw, 112px"
          />
        ) : (
          <div className="bg-twilight text-lake-fog mb-3 flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon size={93} />
          </div>
        )}
        <div className="flex flex-col gap-3 md:gap-2">
          <InternalLink
            to={getSpeciesUrl(speciesId, name)}
            variant="textNone"
            onClick={handleSpeciesClick}
            className="body-text-sm m-0 flex items-center gap-1 font-semibold"
            size="xs"
          >
            {name}
            <ArrowRightIcon width={16} height={16} />
          </InternalLink>
          {loading && <div className="bg-twilight h-4 w-32 animate-pulse rounded" />}
          {hasFishingSpots && (
            <Button
              onClick={handleTopWatersClick}
              variant="buttonSecondary"
              className="w-32 px-2 text-xs"
              size="xs"
            >
              {nrOfFishingSpots ?? data?.entry.totalCount} fishing spots
            </Button>
          )}
        </div>
      </div>
      <AppDownloadModal
        appDownloadSource="TopSpeciesCard"
        appDownloadSourceId={speciesId}
        closeButtonVariant={isMobileScreenWidthOrThinner ? 'textNone' : 'controlWhiteBg'}
        modalHeader={'Know where to fish – find spots by species'}
        modalHeaderClassName="sm:mr-16 md:mr-16"
        modalHeaderVariant="sm"
        modalText=""
        isModalOpen={isAppDownloadModalOpen}
        image={appdownloadPhone}
        imageMobile={appdownloadPhoneMobile}
        imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
        onCloseModal={() => {
          setIsAppDownloadModalOpen(false);
        }}
        textWrapperClassName="mt-6 sm:pb-0 md:pr-4 md:pb-4"
        showQRCodeHint
        width="700px"
      />
    </div>
  );
};
